'use client';

import { apiFetch } from '@/lib/utils/api';
import { MembersRegistryView } from '@slims/ui/organisms';
import { useRouter, useSearchParams } from '@/lib/next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ZodTypeAny } from 'zod';
import type { Member } from '@slims/shared/types';
import { useCERConfig } from '@/hooks/use-cer-config';
import { useMembers } from '@/hooks/use-members';
import { useAdminScopeStore } from '@/stores/admin-scope-store';
import { useNotifications } from '@/hooks/use-notifications';
import type { DBPod } from '@/hooks/use-meters';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { memberSchema } from '@/lib/validations/member-validation';

/**
 * Data wiring for the members registry (Registro Soci). Presentation lives in
 * @slims/ui (MembersRegistryView); this page provides members CRUD, invites,
 * notifications, meters, storage and validation.
 */
export default function MembersPage() {
	const adminConfigId = useAdminScopeStore((s) => s.configId);
	const { members, isLoading, error, addMember, deleteMember } =
		useMembers(adminConfigId);
	const { createNotification } = useNotifications({ orgId: adminConfigId });
	const { config: cerConfig } = useCERConfig();
	const router = useRouter();
	const searchParams = useSearchParams();

	// Pods list, kept live via the meters realtime channel.
	//
	// Legata alla configurazione scelta: con le dipendenze vuote la callback
	// restava sulla prima aperta, e cambiando CER i contatori «POD prelievo /
	// produzione» mostravano quella di prima (T-13, INB-330). Al cambio si
	// svuota subito, e una risposta arrivata tardi per la configurazione
	// precedente non sovrascrive quella nuova.
	const [allPods, setAllPods] = useState<DBPod[]>([]);
	const podsRequestRef = useRef(0);
	const fetchAllPods = useCallback(() => {
		const request = ++podsRequestRef.current;
		apiFetch(`/api/meters?orgId=${adminConfigId}`)
			.then((r) => r.json())
			.then((data: unknown) => {
				if (request !== podsRequestRef.current) return;
				setAllPods(Array.isArray(data) ? (data as DBPod[]) : []);
			})
			.catch(() => {});
	}, [adminConfigId]);

	useEffect(() => {
		setAllPods([]);
		fetchAllPods();
		return subscribeToTable(
			'all-meters-page',
			{ event: '*', schema: 'public', table: 'meters' },
			() => fetchAllPods(),
		);
	}, [fetchAllPods]);

	const uploadToStorage = useCallback(
		async (file: File, path: string): Promise<{ publicUrl: string }> => {
			const formData = new FormData();
			formData.append('file', file);
			formData.append('path', path);
			const res = await apiFetch(`/api/storage/upload?orgId=${adminConfigId}`, { method: 'POST', body: formData });
			const json = await res.json();
			if (!res.ok) throw new Error(json.error);
			return { publicUrl: json.publicUrl };
		},
		// Anche qui: con `[]` un file caricato dopo un cambio di CER finiva
		// nell'organizzazione aperta per prima.
		[adminConfigId],
	);

	// Un socio importato col profilo energetico nasce senza livello 3: la
	// creazione non ha budget per rigenerarlo (vedi `applyCreatedProfile`). Lo si
	// chiede qui, come i due pulsanti della scheda Healthcheck: una richiesta per
	// ambito, ognuna con le sue cinquanta subrequest. Se fallisce, la riga lo dice
	// e la catena notturna riempie comunque i buchi dell'anno indietro.
	const addMemberWithCurves = useCallback(
		async (payload: Record<string, unknown>) => {
			const created = (await addMember(payload as Parameters<typeof addMember>[0])) as Member & {
				profilo?: { ok: boolean; error?: string };
			};
			if (!payload.profiloEnergetico) return created;
			for (const scope of ['storico', 'previsione'] as const) {
				const res = await apiFetch(`/api/meters/health/regenerate?orgId=${adminConfigId}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ memberId: created.id, scope }),
				}).catch(() => null);
				if (res?.ok) continue;
				const error = [created.profilo?.error, 'curve stimate non generate, rigenerale dalla scheda Healthcheck']
					.filter(Boolean)
					.join('; ');
				return { ...created, profilo: { ok: false, error } };
			}
			return created;
		},
		[addMember, adminConfigId],
	);

	const validateMemberField = useCallback((field: string, value: unknown) => {
		const shape = memberSchema.shape as Record<string, ZodTypeAny>;
		const fieldSchema = shape[field];
		if (!fieldSchema) return false;
		return fieldSchema.safeParse(value).success;
	}, []);

	return (
		<MembersRegistryView
			members={members}
			isLoading={isLoading}
			error={error}
			cerNome={cerConfig?.nome}
			allPods={allPods}
			autoOpenAdd={searchParams.get('add') === '1'}
			onAddMember={addMemberWithCurves}
			onDeleteMember={deleteMember}
			onCreateNotification={(payload) =>
				createNotification(payload as Parameters<typeof createNotification>[0])
			}
			onUploadToStorage={uploadToStorage}
			onNavigate={(path) => router.push(path)}
			validateMemberField={validateMemberField}
		/>
	);
}
